import React from 'react';
import { ScrollText, ShieldAlert, CreditCard, Truck, AlertOctagon, Mail, Smartphone } from 'lucide-react';

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-bgmain pb-16 md:pb-24 pt-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Banner */}
        <div className="relative rounded-3xl overflow-hidden bg-gradient-primary text-white p-8 md:p-12 mb-8 shadow-warm grain">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full mb-3 border border-white/10">
              <ScrollText className="w-4 h-4 text-golden" />
              <span className="text-xs font-heading font-medium tracking-wide">POLICY DOCUMENT</span>
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-white">Terms & Conditions</h1>
            <p className="text-white/80 font-body text-xs mt-3 uppercase tracking-wider">Last Updated: July 18, 2026</p>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-3xl border border-borderbeige shadow-card p-6 md:p-10 font-body text-textprimary leading-relaxed space-y-8 animate-fade-in">
          <p className="text-base text-textsecondary">
            Welcome to <strong>Pushpanjali.cloud</strong>. By accessing our website, placing an order, or using our cloud kitchen services, you agree to comply with and be bound by the following terms and conditions. Please read them carefully.
          </p>

          <hr className="border-borderbeige" />

          {/* Section 1 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">1</span>
              Service Eligibility & Account
            </h2>
            <p className="text-textsecondary text-sm font-body">
              To place an order with Pushpanjali, you must be capable of entering into a legally binding contract. You are responsible for ensuring that all details provided during order checkout (name, telephone number, address) are accurate and active.
            </p>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 2: Expanded Shipping & Delivery Policy */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <Truck className="w-5 h-5 text-maroon" />
              Shipping & Delivery Policy
            </h2>
            <div className="text-textsecondary text-sm space-y-2 font-body">
              <p>
                Our services, food delivery, and order completions are currently confined only to select societies in <strong>Noida, Uttar Pradesh, India</strong> (including Amrapali Princely Estate, Amrapali Silicon City, Futech Gateway, Aditya Celebrity Homes, Sector 76, and nearby serviceable locations).
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-xs text-textsecondary mt-2 pl-2">
                <li><strong>Delivery Hours:</strong> Standard food delivery is active during our kitchen operating slots (typically 11:00 AM to 10:00 PM). Orders outside these hours will not be processed.</li>
                <li><strong>Delivery Address & Attempts:</strong> We make a single delivery attempt per order. If the delivery rider reaches your address, is unable to contact you, or is refused entry by society security gate rules, the order will be marked undelivered and no refund will be issued.</li>
                <li><strong>Gate & Lobby Deliveries:</strong> In societies with strict gate restrictions, you may be requested to collect your food delivery packages from the society main gate or ground floor reception lobby.</li>
              </ul>
            </div>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 3 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">3</span>
              FSSAI Compliance & Hygiene
            </h2>
            <p className="text-textsecondary text-sm font-body">
              Pushpanjali follows applicable food safety and hygiene practices prescribed under the Food Safety and Standards Authority of India (FSSAI). Our kitchen's official FSSAI Registration number will be made accessible on our website and food packaging covers.
            </p>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 4: Expanded Payment Terms */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-maroon" />
              Payment Terms
            </h2>
            <div className="text-textsecondary text-sm space-y-2 font-body">
              <p>
                All prices presented on our website are inclusive or exclusive of taxes and delivery rates as indicated in your cart breakdown during checkout. We reserve the right to alter pricing and menus at any time without prior notice.
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-xs text-textsecondary mt-2 pl-2">
                <li><strong>Payment Methods:</strong> We accept online prepaid transactions via secure gateways, including UPI channels, debit/credit cards, and Netbanking.</li>
                <li><strong>Transaction Authorization:</strong> Payments must be fully cleared and authorized before food preparation begins. Pushpanjali is not responsible for transaction failures or delays caused by banking networks.</li>
                <li><strong>Disputes:</strong> Any billing disputes must be brought to our attention via email within 24 hours of placing the order. Verified errors will be credited back to your original source account.</li>
              </ul>
            </div>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 5: Expanded Food Allergen Disclaimer */}
          <section className="space-y-3 bg-saffron/5 p-6 rounded-2xl border border-saffron/20 leading-relaxed text-textprimary">
            <h2 className="font-heading font-bold text-lg text-maroon flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-saffron shrink-0" />
              Food Allergen Disclaimer
            </h2>
            <div className="text-xs text-textsecondary font-body space-y-2">
              <p>
                <strong>Allergen Warning:</strong> Our kitchen prepares dishes that come into contact with common food allergens, including nuts, dairy, wheat, gluten, soy, mustard, sesame, and seeds. While we follow strict sanitization steps, we operate a single-kitchen workspace and cannot guarantee 100% allergen-free meals.
              </p>
              <p>
                Please consult the item descriptions or reach out to us before ordering if you have life-threatening food allergies.
              </p>
              <p className="font-semibold text-textprimary">
                <strong>Consumption recommendation:</strong> Since our dishes are cooked fresh with no preservatives, we strongly advise eating them within **2 hours** of delivery.
              </p>
            </div>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 6 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">6</span>
              Cancellation & Refusal
            </h2>
            <p className="text-textsecondary text-sm font-body">
              We reserve the right to refuse service, cancel orders, or ban client accounts at our sole discretion if we detect fraudulent activity, payment failures, or invalid contact/delivery details.
            </p>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 7 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">7</span>
              Limitation of Liability
            </h2>
            <p className="text-textsecondary text-sm font-body">
              Pushpanjali is not liable for indirect, incidental, or remote damages arising from the consumption of our food or use of our website. In any case, our maximum liability is limited to the exact transaction amount of the order in dispute.
            </p>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 8 */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-maroon/5 text-maroon font-bold text-sm">8</span>
              Governing Law
            </h2>
            <p className="text-textsecondary text-sm font-body">
              These terms are governed by and construed in accordance with the laws of India. Any disputes arising under these conditions will be subject to the exclusive jurisdiction of the competent courts in Noida, Uttar Pradesh.
            </p>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 9: Force Majeure */}
          <section className="space-y-3">
            <h2 className="font-heading font-bold text-xl text-maroon flex items-center gap-2">
              <AlertOctagon className="w-5 h-5 text-maroon" />
              Force Majeure
            </h2>
            <p className="text-textsecondary text-sm font-body">
              Pushpanjali will not be liable for any failure or delay in preparing or delivering orders where such failure arises from causes beyond our reasonable control. This includes acts of God, extreme weather events (storms, heavy monsoon flooding), strikes, lockouts, labor disputes, civil disturbances, society gate closures, pandemic curfews, digital network blackouts, or government regulations.
            </p>
          </section>

          <hr className="border-borderbeige" />

          {/* Section 10: Electronic Communication Consent */}
          <section className="space-y-3 bg-bgmain p-6 rounded-2xl border border-borderbeige">
            <h2 className="font-heading font-bold text-xl text-textprimary flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-maroon" />
              Electronic Communication Consent
            </h2>
            <div className="text-textsecondary text-sm space-y-2 font-body">
              <p>
                By registering an account, checking out, or completing transactions on Pushpanjali.cloud, you consent to receive communications from us electronically.
              </p>
              <p className="text-xs">
                We may communicate with you via **email**, **SMS**, or **WhatsApp messages** to send otp verifications, order dispatch alerts, payment invoices, and delivery updates. You agree that all notices and agreements sent electronically satisfy any legal requirement that such communications be in writing.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
